'use client'

import { motion } from 'framer-motion'

export function PhaseChoosePath() {
  return (
    <section id="execution" className="pb-16 md:pb-24 scroll-mt-16">
      <div className="container max-w-7xl mx-auto px-6">
        <div className="backdrop-blur-2xl bg-white/[0.03] border border-[#3e4658] rounded-[40px] lg:rounded-[60px] p-8 md:p-12 lg:p-16">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-[#61d8fe] text-lg font-anuphan mb-4"
          >
            Phase 2 — Starting at $5,000
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-white text-3xl md:text-[48px] font-normal leading-[1.1] font-gendy mb-6"
          >
            Choose Your Path
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white text-xl md:text-[26px] leading-[1.4] font-anuphan mb-4 max-w-[900px]"
          >
            Strategy without execution is a PowerPoint. After the Blueprint, you choose: have us build it, or learn to build it yourself.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="text-[#bdbdbd] text-lg md:text-[22px] leading-[1.5] font-anuphan mb-12 max-w-[800px]"
          >
            Both paths deliver results. The right choice depends on whether you want speed or sovereignty.
          </motion.p>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Option A: The Build */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/[0.03] border border-[#61d8fe]/30 rounded-[24px] p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-anuphan font-semibold px-3 py-1 rounded-full text-[#61d8fe] bg-[#61d8fe]/10">
                  Recommended
                </span>
                <span className="text-[#5a5f6e] text-xs font-anuphan">Starts at $5,000</span>
              </div>
              <h3 className="text-white text-2xl md:text-[34px] font-normal font-gendy mb-1">The Build</h3>
              <p className="text-[#61d8fe] text-sm font-anuphan mb-4">Done-For-You Execution</p>
              <p className="text-[#8a8f9e] text-sm font-anuphan leading-relaxed mb-6">
                We step in to construct and deploy high-impact workflows and AI agents directly into your operations. We focus on the friction-heavy processes that deliver immediate, tangible ROI.
              </p>

              <div className="space-y-3 mb-6">
                {[
                  'Custom workflow & agent deployment — WhatsApp flows, web-scraping tools, software stack bridging',
                  'Clean architecture — you own your API keys, subscriptions, and data. Zero vendor lock-in.',
                  'Seamless handoff — we build it, we prove it works, and you maintain control',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#61d8fe] mt-1.5" />
                    <span className="text-[#bdbdbd] text-xs font-anuphan">{item}</span>
                  </div>
                ))}
              </div>

              <a
                href="https://calendly.com/alex-behmn/discovery-call"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#544ae8] border border-[#475065] rounded-[100px] px-8 py-3 text-white text-base font-anuphan hover:scale-[1.02] active:scale-[0.98] transition-transform"
              >
                Schedule a Build Consultation
              </a>
            </motion.div>

            {/* Option B: The Academy */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/[0.03] border border-[#3e4658] rounded-[24px] p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-anuphan font-semibold px-3 py-1 rounded-full text-[#bdbdbd] bg-white/[0.05]">
                  One-on-One
                </span>
                <span className="text-[#5a5f6e] text-xs font-anuphan">$5,000/mo</span>
              </div>
              <h3 className="text-white text-2xl md:text-[34px] font-normal font-gendy mb-1">The Academy</h3>
              <p className="text-[#61d8fe] text-sm font-anuphan mb-4">Personalized AI Mastery</p>
              <p className="text-[#8a8f9e] text-sm font-anuphan leading-relaxed mb-6">
                For the leader who wants to master AI themselves. One-on-one enhanced masterclasses tailored to your specific business context — not generic training. You leave each session with actionable skills you can deploy immediately.
              </p>

              <div className="space-y-3 mb-6">
                {[
                  'One-on-one masterclass series — prompt engineering, agent orchestration, tool-specific mastery tailored to your workflows',
                  'Hands-on skill building — you leave each session with frameworks to build and maintain your own automations',
                  'Strategic deep dives — curriculum shaped around your industry, your constraints, your goals',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#61d8fe] mt-1.5" />
                    <span className="text-[#bdbdbd] text-xs font-anuphan">{item}</span>
                  </div>
                ))}
              </div>

              <a
                href="mailto:alex@behmn.com?subject=Academy%20Application%20%E2%80%94%20One-on-One%20AI%20Mastery&body=Hi%20Alex%2C%0A%0AI%E2%80%99m%20interested%20in%20The%20Academy%20%E2%80%94%20one-on-one%20AI%20masterclasses%20(%245%2C000%2Fmo).%0A%0AA%20bit%20about%20me%20and%20what%20I%E2%80%99m%20looking%20to%20learn%3A%0A%0A%5BYour%20name%2C%20role%2C%20and%20what%20you%E2%80%99d%20like%20to%20focus%20on%5D%0A%0ALooking%20forward%20to%20connecting."
                className="inline-block border border-white/30 rounded-[100px] px-8 py-3 text-white text-base font-anuphan hover:bg-white/10 transition-colors"
              >
                Apply for The Academy
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  )
}
